import intl from "react-intl-universal";
import { TbChevronRight } from "react-icons/tb";
import { getCardNetworkLabel } from "../../../../../Components/Atoms/CardNetworkLogo";

const statusStyles = {
  active: "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400",
  draft: "bg-gray-100 text-gray-600 dark:bg-brand-dark-elevated dark:text-gray-400",
  suspended: "bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-400",
};

const authorizerStatusStyles = {
  configured: "text-emerald-600 dark:text-emerald-400",
  pending: "text-amber-600 dark:text-amber-400",
  native: "text-sky-600 dark:text-sky-400",
};

const formatCount = (value) =>
  new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 }).format(value);

const formatGuarantee = (amount, currency, percent) => {
  const formattedAmount = new Intl.NumberFormat(undefined, {
    style: "currency",
    currency,
    notation: "compact",
    maximumFractionDigits: 1,
  }).format(amount);

  return `${formattedAmount} / ${percent}%`;
};

const formatSettlement = (schedule) => {
  if (!schedule) return "—";
  if (schedule.frequency === "realtime") {
    return intl.get("baas.cartoes.programs.settlement.realtime");
  }

  const type = schedule.type || "—";
  const cutoff = schedule.cutoffTime
    ? intl.get("baas.cartoes.programs.settlement.dailyCutoff", { time: schedule.cutoffTime })
    : intl.get("baas.cartoes.programs.settlement.daily");

  return `${type} · ${cutoff}`;
};

const MetricCell = ({ label, value, subValue, valueClassName = "" }) => (
  <div className="min-w-0">
    <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500">
      {label}
    </p>
    <p className={`mt-0.5 truncate text-sm font-semibold text-brand-dark dark:text-gray-100 ${valueClassName}`}>
      {value}
    </p>
    {subValue ? (
      <p className="mt-0.5 truncate text-xs text-gray-500 dark:text-gray-400">{subValue}</p>
    ) : null}
  </div>
);

export const BaasCardProgramCard = ({ program, onSelect }) => {
  const designStatus = program.productDesign?.status ?? "draft";
  const authorizerStatus = program.authorizerConfig?.status ?? "pending";

  return (
    <button
      type="button"
      onClick={() => onSelect(program)}
      className="group flex w-full flex-col rounded-xl border border-gray-200 bg-white text-left transition duration-200 hover:border-gray-300 hover:shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-300 dark:border-brand-dark-border dark:bg-brand-dark-surface dark:hover:border-gray-600 dark:focus-visible:ring-gray-600"
    >
      <div className="flex items-start justify-between gap-3 border-b border-gray-100 px-4 py-3.5 dark:border-brand-dark-border">
        <div className="min-w-0">
          <p className="truncate text-base font-bold text-brand-dark dark:text-gray-100">
            {program.name}
          </p>
          <div className="mt-1.5 flex flex-wrap items-center gap-2">
            {program.instanceName ? (
              <span className="inline-flex items-center rounded-md bg-gray-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-gray-600 dark:bg-brand-dark-elevated dark:text-gray-400">
                {program.instanceName}
              </span>
            ) : null}
            <span className="inline-flex items-center rounded-md bg-gray-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-gray-600 dark:bg-brand-dark-elevated dark:text-gray-400">
              {getCardNetworkLabel(program.productDesign?.network)}
            </span>
            <span
              className={`inline-flex items-center rounded-md px-2 py-0.5 text-[10px] font-semibold ${statusStyles[designStatus] ?? statusStyles.draft}`}
            >
              {intl.get(`baas.cartoes.programs.designStatus.${designStatus}`)}
            </span>
          </div>
        </div>
        <TbChevronRight className="mt-0.5 h-5 w-5 shrink-0 text-gray-300 transition group-hover:text-gray-500 dark:text-gray-600 dark:group-hover:text-gray-400" />
      </div>

      <div className="grid grid-cols-2 gap-x-4 gap-y-3 px-4 py-3.5 sm:grid-cols-3 lg:grid-cols-5">
        <MetricCell
          label={intl.get("baas.cartoes.programs.issuances")}
          value={formatCount(program.issuancesCount)}
          subValue={intl.get("baas.cartoes.programs.issuancesSubtext")}
        />
        <MetricCell
          label={intl.get("baas.cartoes.programs.guarantee")}
          value={formatGuarantee(
            program.guaranteeAmount ?? 0,
            program.guaranteeCurrency || "BRL",
            program.guaranteePercent ?? 0,
          )}
        />
        <MetricCell
          label={intl.get("baas.cartoes.programs.productDesign")}
          value={program.productDesign?.name || program.name}
          subValue={intl.get(`baas.cartoes.programs.designStatus.${designStatus}`)}
        />
        <MetricCell
          label={intl.get("baas.cartoes.programs.authorizer")}
          value={program.authorizerConfig?.provider || "—"}
          subValue={intl.get(`baas.cartoes.programs.authorizerStatus.${authorizerStatus}`)}
          valueClassName={authorizerStatusStyles[authorizerStatus] ?? ""}
        />
        <MetricCell
          label={intl.get("baas.cartoes.programs.settlementLabel")}
          value={formatSettlement(program.settlementSchedule)}
        />
      </div>
    </button>
  );
};
